import { useEffect, useState, type FormEvent } from 'react';
import {
  APPLICATION_STATUS_LABELS,
  APPLICATION_STATUS_VALUES,
  AUTH_MODE_LABELS,
  AUTH_MODE_VALUES,
  type ApplicationWithAccess,
} from '@electrodavisan/shared';
import { Button, Input, Modal, Select, Textarea, useToast } from '../../components/ui';
import { ApiError } from '../../services/api';
import {
  useCreateApplication,
  useUpdateApplication,
  type ApplicationFormValues,
} from '../../services/applications.service';

const EMPTY: ApplicationFormValues = {
  name: '',
  slug: '',
  shortDescription: '',
  description: '',
  iconUrl: '',
  launchUrl: '',
  category: '',
  status: 'COMING_SOON',
  authMode: 'OIDC',
  oidcClientId: '',
  healthUrl: '',
  sortOrder: 0,
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function ApplicationFormModal({
  open,
  application,
  onClose,
}: {
  open: boolean;
  /** `null` = alta; con valor = edicion. */
  application: ApplicationWithAccess | null;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const create = useCreateApplication();
  const update = useUpdateApplication();

  const [values, setValues] = useState<ApplicationFormValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSlugTouched(Boolean(application));
    setValues(
      application
        ? {
            name: application.name,
            slug: application.slug,
            shortDescription: application.shortDescription ?? '',
            description: application.description ?? '',
            iconUrl: application.iconUrl ?? '',
            launchUrl: application.launchUrl ?? '',
            category: application.category ?? '',
            status: application.status,
            authMode: application.authMode,
            oidcClientId: application.oidcClientId ?? '',
            healthUrl: application.healthUrl ?? '',
            sortOrder: application.sortOrder,
          }
        : EMPTY,
    );
  }, [open, application]);

  const set = <K extends keyof ApplicationFormValues>(
    key: K,
    value: ApplicationFormValues[K],
  ): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    const next: Record<string, string> = {};
    if (values.name.trim().length < 2) next.name = 'Indica el nombre de la aplicacion.';
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(values.slug)) {
      next.slug = 'Solo minusculas, numeros y guiones.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    // Los campos opcionales vacios viajan como null, no como cadena vacia:
    // la API valida URLs y "" no es una URL valida.
    const payload: ApplicationFormValues = {
      ...values,
      name: values.name.trim(),
      shortDescription: values.shortDescription?.trim() || null,
      description: values.description?.trim() || null,
      iconUrl: values.iconUrl?.trim() || null,
      launchUrl: values.launchUrl?.trim() || null,
      category: values.category?.trim() || null,
      oidcClientId: values.oidcClientId?.trim() || null,
      healthUrl: values.healthUrl?.trim() || null,
      sortOrder: Number(values.sortOrder) || 0,
    };

    const onError = (error: unknown): void => {
      toast.error(
        'No se pudo guardar',
        error instanceof ApiError ? error.message : 'Revisa los datos e intentalo de nuevo.',
      );
    };

    if (application) {
      update.mutate(
        { id: application.id, values: payload },
        {
          onSuccess: () => {
            toast.success('Aplicacion actualizada');
            onClose();
          },
          onError,
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success('Aplicacion registrada');
          onClose();
        },
        onError,
      });
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={application ? 'Editar aplicacion' : 'Registrar aplicacion'}
      description="Los accesos se configuran despues, desde la pestana de accesos."
      className="sm:max-w-2xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button form="application-form" type="submit" loading={pending}>
            {application ? 'Guardar cambios' : 'Registrar'}
          </Button>
        </>
      }
    >
      <form id="application-form" onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Nombre"
          required
          value={values.name}
          error={errors.name}
          onChange={(event) => {
            set('name', event.target.value);
            if (!slugTouched) set('slug', slugify(event.target.value));
          }}
        />
        <Input
          label="Identificador (slug)"
          required
          value={values.slug}
          error={errors.slug}
          onChange={(event) => {
            setSlugTouched(true);
            set('slug', event.target.value);
          }}
          hint="Se usa en URLs internas. No se puede repetir."
        />

        <Input
          label="Descripcion corta"
          containerClassName="sm:col-span-2"
          value={values.shortDescription ?? ''}
          onChange={(event) => set('shortDescription', event.target.value)}
          maxLength={255}
        />

        <Textarea
          label="Descripcion"
          containerClassName="sm:col-span-2"
          value={values.description ?? ''}
          onChange={(event) => set('description', event.target.value)}
          rows={3}
        />

        <Input
          label="URL de acceso (launch_url)"
          type="url"
          value={values.launchUrl ?? ''}
          onChange={(event) => set('launchUrl', event.target.value)}
          placeholder="https://"
          hint="A donde lleva el boton Abrir aplicacion."
        />
        <Input
          label="URL del icono"
          // `text` y no `url`: lo normal aqui es una ruta del propio sitio,
          // como `/aplicaciones/ed-calendar.png`, y el navegador rechaza
          // cualquier valor que no empiece por un esquema. Con `url` no se
          // podia guardar ninguna aplicacion, ni siquiera sin tocar el campo.
          type="text"
          value={values.iconUrl ?? ''}
          onChange={(event) => set('iconUrl', event.target.value)}
          placeholder="/aplicaciones/ejemplo.png"
          hint="Una ruta del sitio o una direccion https:// completa."
        />

        <Input
          label="Categoria"
          value={values.category ?? ''}
          onChange={(event) => set('category', event.target.value)}
          placeholder="Operaciones, Finanzas, ..."
        />
        <Input
          label="Orden"
          type="number"
          min={0}
          max={9999}
          value={values.sortOrder}
          onChange={(event) => set('sortOrder', Number(event.target.value))}
        />

        <Select
          label="Estado"
          value={values.status}
          onChange={(event) => set('status', event.target.value)}
          options={APPLICATION_STATUS_VALUES.map((value) => ({
            value,
            label: APPLICATION_STATUS_LABELS[value],
          }))}
        />
        <Select
          label="Modo de autenticacion"
          value={values.authMode}
          onChange={(event) => set('authMode', event.target.value)}
          options={AUTH_MODE_VALUES.map((value) => ({
            value,
            label: AUTH_MODE_LABELS[value],
          }))}
          hint="OIDC es el modo recomendado para el SSO del ecosistema."
        />

        <Input
          label="Client ID en Keycloak"
          value={values.oidcClientId ?? ''}
          onChange={(event) => set('oidcClientId', event.target.value)}
          placeholder="mi-aplicacion-web"
          hint="Solo informativo: el client se crea en Keycloak."
        />
        <Input
          label="URL de health check"
          type="url"
          value={values.healthUrl ?? ''}
          onChange={(event) => set('healthUrl', event.target.value)}
          placeholder="https://"
        />
      </form>
    </Modal>
  );
}
